import { WELLNESS_TOOLKIT_PROMOTION } from './types'
import { ProviderProgramTaxonomy } from '../../models/providers/Providers'
import {
  DISASTER_RESPONSE_REFERRAL_SOURCE,
  DISASTER_RESPONSE_SUPPORT_SCHEDULE_CALL_URL,
  ONCEHUB_SCHEDULE_CALL_URL_PARAMS,
} from '../common/constants/constants'
import { SELF_CARE_MEDIA_EVENTS, SELF_CARE_MEDIA_EVENTS_PROPERTIES } from '../common/constants/mixpanelConstants'
import { ESSENTIALS_CONTENT_CATEGORY } from '../essentials/types'
import { COACHING_PROGRAM } from '../programConfigs/programs/coaching'
import { isMinor } from '../searchForCare/utils'
import { SelfCareMediaContent } from '../selfCareMedia/types'

export const getDefaultAudioMixpanelProperties = (media: SelfCareMediaContent) => {
  return {
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.DESTINATION_URL]: media.articleSubPath,
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.CONTENT_ID]: media.id,
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.CONTENT_FORMAT]: media.contentFormat,
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.CONTENT_TYPE]: media.contentType,
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.ESSENTIALS]: true,
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.LIBRARY_NAME]: 'Essentials',
  }
}

export const getAudioPlayMixpanelProperties = (media: SelfCareMediaContent, duration: number) => {
  return {
    ...getDefaultAudioMixpanelProperties(media),
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.TITLE]: media.title,
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.URL_SLUG]: media.slug,
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.DURATION]: duration,
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.FROM_LYRA]: true,
  }
}

const getDiscoverMediaMixpanelProperties = (subPath: string) => {
  return {
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.DESTINATION_URL]: subPath,
  }
}

const getDefaultMixpanelPropertiesWithUrl = (subPath: string, media?: SelfCareMediaContent) => {
  return {
    ...(media ? getDefaultAudioMixpanelProperties(media) : {}),
    [SELF_CARE_MEDIA_EVENTS_PROPERTIES.DESTINATION_URL]: subPath,
  }
}

export const getNavigateToEssentialMixpanelProperties = (
  event: SELF_CARE_MEDIA_EVENTS,
  subPath: string,
  media?: SelfCareMediaContent,
) => {
  switch (event) {
    case SELF_CARE_MEDIA_EVENTS.CLICK_DISCOVER: {
      return getDiscoverMediaMixpanelProperties(subPath)
    }
    case SELF_CARE_MEDIA_EVENTS.CLICK_ESSENTIALS_VIDEO:
    case SELF_CARE_MEDIA_EVENTS.CLICK_SHOW_TRANSCRIPT: {
      return getDefaultMixpanelPropertiesWithUrl(subPath, media)
    }
  }

  return {}
}

export const getExploreMoreEssentialSubPath = ({
  slug,
  category = ESSENTIALS_CONTENT_CATEGORY.ARTICLE,
}: {
  slug?: string
  category?: ESSENTIALS_CONTENT_CATEGORY
}) => {
  const categoryStr = `${category}s`
  return slug ? `/${categoryStr}/${slug}` : ''
}

/**
 * Referrer sent to the Library so its mixpanel events can be attributed back to the specific
 * toolkit the member came from, e.g. `work toolkit` vs `relationships toolkit`.
 */
export const getWellnessToolkitEssentialsReferrer = (promotion: WELLNESS_TOOLKIT_PROMOTION) => `${promotion} toolkit`

export type WellnessToolkitTitleCardRoute =
  | { destination: 'family-care-options' }
  | { destination: 'direct-path' }
  | {
      destination: 'program-aware-triage'
      isSearchToolkitAware: true
      programPreference?: never
    }
  | {
      destination: 'program-aware-triage'
      isSearchToolkitAware: false
      programPreference: ProviderProgramTaxonomy
    }

export const getWellnessToolkitTitleCardRoute = ({
  promotion,
  isLyraSelect,
}: {
  promotion: WELLNESS_TOOLKIT_PROMOTION
  isLyraSelect: boolean
}): WellnessToolkitTitleCardRoute => {
  if (promotion === WELLNESS_TOOLKIT_PROMOTION.FAMILY) {
    return { destination: 'family-care-options' }
  }

  if (promotion === WELLNESS_TOOLKIT_PROMOTION.PARENT && !isLyraSelect) {
    return { destination: 'direct-path' }
  }

  if (promotion === WELLNESS_TOOLKIT_PROMOTION.TRAUMA) {
    return {
      destination: 'program-aware-triage',
      isSearchToolkitAware: true,
    }
  }

  return {
    destination: 'program-aware-triage',
    isSearchToolkitAware: false,
    programPreference: { ...COACHING_PROGRAM.directBookingParams },
  }
}

/**
 * Builds the disaster response support OnceHub scheduling URL with all query params.
 * Always sets the `referralsource`, then appends the user's Salesforce contact ID when
 * available so the booking can be attributed to them. Falls back to the user's first
 * name and email when there is no Salesforce ID.
 */
export const getDisasterResponseSupportScheduleCallUrl = ({
  salesforceId,
  firstName,
  email,
}: {
  salesforceId?: string
  firstName?: string
  email?: string
}): string => {
  const url = new URL(DISASTER_RESPONSE_SUPPORT_SCHEDULE_CALL_URL)
  url.searchParams.set(ONCEHUB_SCHEDULE_CALL_URL_PARAMS.REFERRAL_SOURCE, DISASTER_RESPONSE_REFERRAL_SOURCE)
  if (salesforceId) {
    url.searchParams.set(ONCEHUB_SCHEDULE_CALL_URL_PARAMS.SOSF_CONTACT_ID, salesforceId)
  } else {
    if (firstName) {
      url.searchParams.set(ONCEHUB_SCHEDULE_CALL_URL_PARAMS.NAME, firstName)
    }
    if (email) {
      url.searchParams.set(ONCEHUB_SCHEDULE_CALL_URL_PARAMS.EMAIL, email)
    }
  }
  return url.toString()
}

/**
 * Determines whether the disaster response benefit should be available for a user.
 * It is gated behind the `isDisasterResponseServiceEnabled` flag and limited to adult users (18+).
 * Users with an unknown date of birth default to eligible (show rather than hide).
 */
export const getIsDisasterResponseServiceAvailableForUser = (
  isDisasterResponseServiceEnabled: boolean,
  userDob: string | undefined,
): boolean => {
  if (!isDisasterResponseServiceEnabled) return false
  return !isMinor(userDob ?? '')
}
